import React, {useState} from "react";
import {Button, Input, Modal, Spin} from "antd";
import TfButton from "../../tf-button/TfButton";
import {CheckCircleFilled, CloseCircleOutlined, InfoCircleTwoTone} from "@ant-design/icons";
import {CONFIG} from "../../../util/config";
import styles from "./ChangePassword.module.scss";

type PasswordInfo = {
    oldPassword: string,
    newPassword: string,
    confirmNewPassword: string
}

const infoIcon = <InfoCircleTwoTone className={styles.infoIconSmall} />
const successIcon = <CheckCircleFilled className={styles.successIconSmall} />
const dangerIcon = <CloseCircleOutlined className={styles.errorIconSmall} />

export class UnauthorisedError extends Error {
    constructor(message = "Unauthorized") {
        super(message);
        Object.setPrototypeOf(this, new.target.prototype);
    }
}

export class BadRequestError extends Error {
    constructor(message = "Bad Request") {
        super(message);
        Object.setPrototypeOf(this, new.target.prototype);
    }
}

/**
 * @throws UnauthorisedError if oldpassword doesn't match users old password
 * @throws BadRequestError if newpassword doesn't backend password requirements
 */
const updatePassword  = ({oldPassword, newPassword}: {oldPassword: string, newPassword: string}) : Promise<void> => {
    const body = {
        oldPassword,
        newPassword
    }
    return fetch(CONFIG.restPrefix + '/login/change-password', {
        method: 'POST',
        body: JSON.stringify(body),
        headers: {
            'Accept': 'application/json',
            'Content-Type': 'application/json',
        },
        ...(CONFIG.isDev && { credentials: 'include' })
    })
        .then(response => {
            if (response.status === 400) {
                throw new BadRequestError()
            }
            if (response.status === 401) {
                throw new UnauthorisedError()
            }
            if (response.status !== 200) {
                throw new Error()
            }
            return
        })

}

const ChangePassword = ({open, onSuccess} : {open: boolean, onSuccess: () => void}) => {

    const [sendStatus, setSendStatus] = useState<"START" | "SENDING" | "SENT" | "ERROR">("START");
    const [updateError, setUpdateError] = useState("")
    const [passwordInfo, setPasswordInfo] = useState<PasswordInfo>({
        oldPassword: "",
        newPassword: "",
        confirmNewPassword: ""
    });

    const onSubmit = () => {
        setSendStatus("SENDING")
        updatePassword({...passwordInfo})
            .then(() => {
                setSendStatus("SENT")
            })
            .catch(e => {

                if (e instanceof UnauthorisedError) {
                    setSendStatus("START")
                    setUpdateError("The current password you entered is incorrect")
                    return
                }
                if (e instanceof BadRequestError) {
                    setSendStatus("START")
                    setUpdateError("Unfortunately the new password doesn't meet security requirements. Please use a new password")
                    return;
                }

                setSendStatus("ERROR")
            })
    }

    const onChange = (event: React.ChangeEvent<HTMLInputElement>) => {
        const { name, value } = event.target;
        const key = name as keyof PasswordInfo;

        setPasswordInfo(prev => ({
            ...prev,
            [key]: value
        }));
    }

    const newPassword = passwordInfo.newPassword

    const isTwelveCharactersValid = newPassword.length >= 12
    const isUppercaseValid = /.*[A-Z].*/.test(newPassword)
    const isLowercaseValid = /.*[a-z].*/.test(newPassword)
    const isTwoNumbersValid = /.*[0-9].*[0-9].*/.test(newPassword)
    const doesntMatchOldPassword = passwordInfo.oldPassword !== passwordInfo.newPassword

    const twelveCharactersIcon = isTwelveCharactersValid ? successIcon : dangerIcon
    const uppercaseIcon = isUppercaseValid ? successIcon : dangerIcon
    const lowercaseIcon = isLowercaseValid ? successIcon : dangerIcon
    const twoNumbersIcon = isTwoNumbersValid ? successIcon : dangerIcon
    const doesntMatchOldPasswordIcon = doesntMatchOldPassword ? successIcon : dangerIcon

    const isSubmitDisabled = (() => {
        if (passwordInfo.newPassword !== passwordInfo.confirmNewPassword) {
            return true
        }
        if (!isTwelveCharactersValid) {
            return true
        }
        if (!isUppercaseValid) {
            return true
        }
        if (!isLowercaseValid) {
            return true
        }
        if (!isTwoNumbersValid) {
            return true
        }
        if (!doesntMatchOldPasswordIcon) {
            return true
        }
        return false
    })()

    const loadPage = (() => {
        if (sendStatus === "START") {
            return <>
                <p>Your password has expired. Please enter a new password.</p>
                <p>It must contain:</p>
                <div className={styles.changePasswordIconList}>
                    <div>
                        {newPassword.length === 0 ? infoIcon : twelveCharactersIcon} <span>At least 12 characters</span>
                    </div>
                    <div>
                        {newPassword.length === 0 ? infoIcon : uppercaseIcon} <span>At least one upper-case letter</span>
                    </div>
                    <div>
                        {newPassword.length === 0 ? infoIcon : lowercaseIcon} <span>At least one lower-case letter</span>
                    </div>
                    <div>
                        {newPassword.length === 0 ? infoIcon : twoNumbersIcon} <span>Minimum 2 numbers</span>
                    </div>
                    <div>
                        {newPassword.length === 0 ? infoIcon : doesntMatchOldPasswordIcon} <span>New password can not be the same as old password</span>
                    </div>
                </div>
                <div className={styles.changePasswordInputWrapper}>
                    <div>
                        <p className={styles.changePasswordParagraph}>Confirm Current Password</p>
                        <Input.Password  name={"oldPassword"} onChange={onChange}/>
                    </div>
                    <div>
                        <p className={styles.changePasswordParagraph}>New Password</p>
                        <Input.Password name={"newPassword"} onChange={onChange} />
                    </div>
                    <div>
                        <p className={styles.changePasswordParagraph}>Confirm New Password</p>
                        <Input.Password name={"confirmNewPassword"} onChange={onChange} />
                    </div>
                </div>
                <TfButton disabled={isSubmitDisabled} onClick={onSubmit}>Change Password</TfButton>

                {passwordInfo.confirmNewPassword.length > 0 &&
                    passwordInfo.newPassword !== passwordInfo.confirmNewPassword &&
                        <p className={styles.changePasswordError}>
                            New password and new password confirmation do not match!
                        </p>
                }
                {updateError && <p className={styles.changePasswordError}>{updateError}</p>}
            </>
        }
        if (sendStatus === "SENDING") {
            return <>
                <div className={styles.loadingWrapper}>
                    <Spin/>
                    <span>Sending email...</span>
                </div>
            </>
        }
        if (sendStatus === "SENT") {
            return <>
                <div className={styles.statusWrapper}>
                    <CheckCircleFilled className={styles.successIconBig}/>
                    <p className={styles.changePasswordParagraph}>
                        Your password has been reset. Please close this box and login using the updated details.
                    </p>
                    <Button onClick={onSuccess}>Close</Button>
                </div>
            </>
        }
        if (sendStatus === "ERROR") {
            return <>
                <div className={styles.statusWrapper}>
                    <CloseCircleOutlined className={styles.errorIconBig}/>
                    <p className={styles.changePasswordParagraph}>
                        There was an error updating your password. For assistance please contact
                        <a href="mailto:support@healthiq.co.uk">
                            <strong>Vantage Support team</strong>
                        </a>
                    </p>
                </div>
            </>
        }
    })


    return <Modal
        title={"Reset Password"}
        open={open}
        closable={sendStatus === "SENT"}
        onCancel={() => {
            if (sendStatus === "SENT") {
                onSuccess()
            }
        }}
        footer={null}
        width={500}
    >
        {loadPage()}
    </Modal>
}

export default ChangePassword;